import { Alert, AlertDescription, AlertIcon, Box, Heading } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  type NegotiationRoundDto,
  type SourcingEventDto,
  type ViewDocumentAsOwnerDto,
} from "../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { StepNavigation } from "../../../../common/StepNavigation";

interface Props {
  event: SourcingEventDto;
  round: NegotiationRoundDto;
  refetchRound: () => void;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const RoundDocumentsStep = ({ event, round, refetchRound, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();

  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityType: "NegotiationRoundV2",
    entityId: round.id,
    eventId: event.id,
    reloadEntity: refetchRound,
  });

  useEffect(() => {
    setDocuments(round.documents ?? []);
  }, [round.documents, setDocuments]);

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Documents")}
      </Heading>

      {round.status === NegotiationRoundStatus.Published && (
        <Alert status="info" rounded="lg" mb="8">
          <AlertIcon />
          <AlertDescription>{t("Documents you upload will be visible to invited suppliers")}</AlertDescription>
        </Alert>
      )}

      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
          removeUpload({
            status: UploadCompletedStatus.UploadCompleted,
            documentId: document.id,
            uploadId: document.fromUploadId,
            ...document,
          })
        }
        canEditTextDocuments={false}
        canDeleteDocuments
        canEditDocumentNames
        isTagsColumnVisible
        documentUploadConfig={{
          uppy,
          dropTargetId: `round-documents-${round.id}`,
          dropTargetHelperText: t("Upload documents for this negotiation round"),
          uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
          removeUpload,
        }}
      />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
